import React, { useState, useMemo } from 'react';
import { WebSocketAdapter } from '../../utils/WebSocketAdapter';
import { DesktopLayout, StandingsSidebar, LiveAnswer as SharedLiveAnswer, AnswerOptions, ImageWithFallback, QuestionDisplay, TimerSection, DrunkVisualPuzzle } from '../shared';
import { useQuestionFlip, useTimerFlipTrigger } from '../../hooks/useQuestionFlip';
import { Question } from '../../types/game';
import type { GameStatistics } from '../../types/game';
import { usePartyHostGameLifecycle } from '../../hooks/usePartyHostGameLifecycle';
import { pickRandomImageAnimation } from '../../constants/drunkVisionAnimations';
import {
  PartyHostCorrectAnswerCard,
  PartyHostGameEnd,
  PartyHostPlayingChrome,
  PartyHostResultFrame,
  PartyHostWaiting,
} from './PartyHostShell';
import { partyHostCssTheme } from '../../themes/cssTheme';
import '../../App.css';
import '../../styles/game-layout.css';
import '../../styles/drunk-vision.css';
import { useTranslation } from '../../i18n';

interface AreYouDrunkQuestion {
  questionId?: string;
  id?: string;
  question: string;
  options: string[];
  timeLimit: number;
  questionNumber: number;
  totalQuestions: number;
  startTime: number;
  imageUrl?: string;
  fallbackImageUrl?: string;
  visualContent?: string;
  visualKind?: 'emoji-grid' | 'odd-one-out' | 'symbol-sequence' | 'word-trap' | 'memory' | 'still-object';
  visualItems?: string[];
  visualBehavior?: 'memory' | 'still-object';
  stillItemIndex?: number;
  memoryHideAfterMs?: number;
  memoryRevealAtMs?: number;
  memoryRevealDurationMs?: number;
  isPicturePerfect?: boolean;
  isAreYouDrunk?: boolean;
  interesting?: string;
}

interface QuestionResult {
  questionId: string;
  correctAnswer: string;
  submissions: Array<{
    playerId: string;
    answer: string;
    responseTime: number;
    isCorrect: boolean;
    points: number;
  }>;
  leaderboard: Array<{
    playerId: string;
    playerName: string;
    score: number;
  }>;
  isAreYouDrunk?: boolean;
}

interface LiveAnswer {
  playerId: string;
  playerName: string;
  avatar?: string;
  timestamp: number;
  answer?: string;
}

interface AreYouDrunkHostDisplayProps {
  roomState: {
    roomName: string;
    gameType: string;
    hostId: string;
    players: Array<{
      id: string;
      name: string;
      score: number;
      avatar?: string;
    }>;
    gameState: 'waiting' | 'playing' | 'finished';
    currentQuestion?: AreYouDrunkQuestion;
    finalLeaderboard?: Array<{ playerId: string; playerName: string; score: number }>;
    gameStatistics?: GameStatistics | null;
  };
  socket: WebSocketAdapter;
  onBackToGameSelection?: () => void;
  myPlayerId?: string;
  hostIsPlaying?: boolean;
  onGoHome?: () => void;
}

const THEME = partyHostCssTheme;

const AreYouDrunkHostDisplay: React.FC<AreYouDrunkHostDisplayProps> = ({
  roomState,
  socket,
  onBackToGameSelection,
  myPlayerId,
  hostIsPlaying = false,
  onGoHome,
}) => {
  const { t } = useTranslation();
  const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);
  const [answerCorrect, setAnswerCorrect] = useState<boolean | null>(null);
  const {
    currentQuestion,
    timeRemaining,
    questionResult,
    gameEnded,
    finalLeaderboard,
    gameStatistics,
    waitingForNewGame,
    liveAnswers,
    answerSubmitted,
    gameEndData,
    playersRef,
    submitAnswer,
    restartGame: handleRestartGame,
    playSameGame,
    setAnswerSubmitted,
  } = usePartyHostGameLifecycle<
    AreYouDrunkQuestion,
    QuestionResult,
    { questionId: string; isCorrect: boolean; points: number }
  >({
    roomState,
    socket,
    hostIsPlaying,
    isQuestionForGame: (question) => !!question.isAreYouDrunk || !!question.isPicturePerfect,
    isResultForGame: (result) => !!result.isAreYouDrunk,
    onResetHostPlaying: () => {
      setSelectedAnswer(null);
      setAnswerCorrect(null);
    },
    onAnswerReceived: (data) => {
      if (!hostIsPlaying) return;
      setAnswerCorrect(data.isCorrect);
      setAnswerSubmitted(true);
    },
  });

  /** One random drunk-vision image animation per question */
  const drunkImageAnimation = useMemo(
    () => pickRandomImageAnimation(),
    [currentQuestion?.questionId ?? currentQuestion?.id]
  );

  // Question flip animation
  const flipState = useQuestionFlip({
    currentQuestion,
    liveAnswers,
    totalPlayers: roomState.players.length,
    answerSubmitted,
    hostIsPlaying,
  });

  // Trigger flip when timer runs out
  useTimerFlipTrigger(
    timeRemaining,
    currentQuestion,
    flipState.showQuestionFlip,
    flipState.setShowQuestionFlip,
    flipState.previousQuestionIdForFlipRef
  );

  const handleAnswerClick = (answer: string) => {
    if (answerSubmitted || !currentQuestion) return;
    setSelectedAnswer(answer);
    if (!submitAnswer(answer)) setSelectedAnswer(null);
  };

  const getPlayerAvatar = (playerId: string): string | undefined => {
    const player = playersRef.current.find((p) => p.id === playerId);
    return player?.avatar;
  };

  // Fullscreen wrapper styles removed — party-host shell CSS

  if (gameEnded) {
    return (
      <PartyHostGameEnd
        roomName={roomState.roomName}
        gameType={roomState.gameType}
        finalLeaderboard={finalLeaderboard}
        gameStatistics={gameStatistics}
        gameEndData={gameEndData}
        onPlaySameGame={playSameGame}
        onBackToGameSelection={onBackToGameSelection}
        getPlayerAvatar={getPlayerAvatar}
      />
    );
  }

  if (waitingForNewGame || (!currentQuestion && !questionResult)) {
    return (
      <PartyHostWaiting
        roomName={roomState.roomName}
        emoji="🍺"
        onBackToGameSelection={onBackToGameSelection}
      >
        <div className="party-host-waiting__bounce" aria-hidden>
          <span className="party-host-waiting__bounce-item">🍻</span>
          <span className="party-host-waiting__bounce-item">🍷</span>
          <span className="party-host-waiting__bounce-item">🥃</span>
        </div>
      </PartyHostWaiting>
    );
  }

  if (questionResult) {
    const resultSharedLiveAnswers: SharedLiveAnswer[] = liveAnswers.map((la) => ({
      playerId: la.playerId,
      playerName: la.playerName,
      avatar: la.avatar,
      timestamp: la.timestamp,
      answer: la.answer !== undefined ? String(la.answer) : undefined,
    }));

    return (
      <PartyHostResultFrame roomName={roomState.roomName} onBackToGameSelection={onBackToGameSelection}>
        <DesktopLayout
          leftContent={
            <div className="party-host-correct-answer__wrap">
              <PartyHostCorrectAnswerCard correctAnswer={questionResult.correctAnswer} />
            </div>
          }
          rightContent={
            <StandingsSidebar
              players={roomState.players}
              liveAnswers={resultSharedLiveAnswers}
              totalPlayers={roomState.players.length}
              questionId={questionResult.questionId}
              shouldUpdateScores={true}
              theme={THEME}
            />
          }
          mobileOnlyContent={
            <StandingsSidebar
              players={roomState.players}
              liveAnswers={resultSharedLiveAnswers}
              totalPlayers={roomState.players.length}
              questionId={questionResult.questionId}
              shouldUpdateScores={true}
              isMobile={true}
              theme={THEME}
            />
          }
          rightWidth="320px"
        />
      </PartyHostResultFrame>
    );
  }

  const progress = currentQuestion ? (timeRemaining / currentQuestion.timeLimit) * 100 : 0;
  const secondsRemaining = Math.ceil(timeRemaining / 1000);

  const sharedLiveAnswers: SharedLiveAnswer[] = liveAnswers.map((la) => ({
    playerId: la.playerId,
    playerName: la.playerName,
    avatar: la.avatar,
    timestamp: la.timestamp,
    answer: la.answer,
  }));

  return (
    <PartyHostPlayingChrome
      gameLabel={roomState.gameType === 'vision-check' ? '👁️ Vision Check' : '🍺 Are You Drunk?'}
      roomName={roomState.roomName}
      theme={THEME}
      onBackToGameSelection={onBackToGameSelection}
      onRestart={handleRestartGame}
    >
      {currentQuestion && (
        <div className="game-host-layout">
          <div
            className={`game-host-left ${roomState.gameType === 'vision-check' ? 'vision-check-host' : ''} ${!currentQuestion.imageUrl && !currentQuestion.fallbackImageUrl && !currentQuestion.visualContent ? 'no-image' : ''} ${!hostIsPlaying ? 'host-only' : ''}`}
          >
            <div className="game-host-timer-section">
              <TimerSection
                questionNumber={currentQuestion.questionNumber}
                totalQuestions={currentQuestion.totalQuestions}
                timeSeconds={secondsRemaining}
                progress={progress}
                primaryColor={THEME.primary}
              />
            </div>

            {(currentQuestion.imageUrl || currentQuestion.fallbackImageUrl) && (
              <div className="game-host-image-section">
                <div className="game-image-container">
                  <div className={`drunk-img-wrap ${drunkImageAnimation}`}>
                    <ImageWithFallback
                      key={currentQuestion.questionId || currentQuestion.id}
                      src={currentQuestion.imageUrl || ''}
                      fallbackSrc={currentQuestion.fallbackImageUrl}
                      alt={t('gameplay.imageAlt.guessPicture')}
                    />
                  </div>
                </div>
              </div>
            )}

            {(currentQuestion.visualContent || currentQuestion.visualItems?.length) && (
              <div className="game-host-image-section">
                <div className="game-image-container">
                  <DrunkVisualPuzzle
                    visualContent={currentQuestion.visualContent}
                    visualItems={currentQuestion.visualItems}
                    visualKind={currentQuestion.visualKind}
                    visualBehavior={currentQuestion.visualBehavior}
                    stillItemIndex={currentQuestion.stillItemIndex}
                    memoryHideAfterMs={currentQuestion.memoryHideAfterMs}
                    memoryRevealAtMs={currentQuestion.memoryRevealAtMs}
                    memoryRevealDurationMs={currentQuestion.memoryRevealDurationMs}
                    questionStartTime={currentQuestion.startTime}
                    timeLimit={currentQuestion.timeLimit}
                    animationClass={drunkImageAnimation}
                  />
                </div>
              </div>
            )}

            <div
              className={`game-host-question-section${flipState.showQuestionFlip || flipState.isFadingOut ? ' game-host-question-section--showing-fact' : ''}`}
            >
              <QuestionDisplay
                question={currentQuestion as Question}
                showQuestionFlip={flipState.showQuestionFlip}
                isFadingOut={flipState.isFadingOut}
                previousQuestionIdForFlipRef={flipState.previousQuestionIdForFlipRef}
                showHint={false}
                gameType={roomState.gameType}
                drunkVision={true}
              />
            </div>

            {hostIsPlaying && (
              <div className="game-host-answers-section">
                <AnswerOptions
                  question={currentQuestion as Question}
                  selectedAnswer={selectedAnswer}
                  answerSubmitted={answerSubmitted}
                  hostIsPlaying={hostIsPlaying}
                  showHint={false}
                  gameType={roomState.gameType}
                  onAnswerSelect={(answer) => {
                    if (!answerSubmitted) setSelectedAnswer(answer);
                  }}
                  onSubmitAnswer={() => {
                    if (selectedAnswer && currentQuestion && !answerSubmitted) {
                      handleAnswerClick(selectedAnswer);
                    }
                  }}
                  answerCorrect={answerCorrect}
                  waitingForOthers={answerSubmitted && !questionResult}
                  isSubmitting={answerSubmitted}
                />
              </div>
            )}
          </div>

          <div className="game-host-right">
            <StandingsSidebar
              players={roomState.players}
              liveAnswers={sharedLiveAnswers}
              totalPlayers={roomState.players.length}
              questionId={currentQuestion.questionId || currentQuestion.id}
              shouldUpdateScores={false}
              theme={{
                primary: THEME.primary,
                primaryLight: THEME.primaryLight,
                accent: THEME.accent,
              }}
            />
          </div>
        </div>
      )}
    </PartyHostPlayingChrome>
  );
};

export default AreYouDrunkHostDisplay;
